import { Hidden } from '@mui/material'
import Image from 'next/image'
import React, { useState } from 'react'
import CompareButton from 'src/components/global/CompareButton'
import { EnhancedTag } from 'src/components/global/EnhancedTag/EnhancedTag'
import { FollowButton } from 'src/components/global/FollowButton'
import { HtmlContentViewer } from 'src/components/global/HtmlContentViewer/HtmlContentViewer'
import { PremiumTag } from 'src/components/global/PremiumTag/PremiumTag'
import { ReviewWithLinkItem } from 'src/components/global/ReviewWithLinkItem'
import { SignUpModal } from 'src/components/global/SignUpModal'
import { ThemeOnly } from 'src/components/global/ThemeOnly'
import { VerifyEmailModal } from 'src/components/global/VerifyEmailModal'
import { SHOW_REVIEWS } from 'src/config/config'
import { ISolutionListItemFragment, ISubTopicItemFragment, ITopicItemFragment } from 'src/graphql/generated/hooks'
import { useAuthenticated } from 'src/hooks/useAuthenticated'
import { convertSolutionItemTypeToSolutionType } from 'src/utils/convertSolutionItemTypeToSolutionType'
import { removeTags } from 'src/utils/removeTags'

import { LinkWithOrigin } from '../../LinkWithOrigin/LinkWithOrigin'
import CategoryList from './CategoryList'
import VendorLogo from './VendorLogo'

export interface VendorListItemProps {
  item: ISolutionListItemFragment
  href: string
  topic?: ITopicItemFragment
  subTopic?: ISubTopicItemFragment
  companyName?: string
  showCompare?: boolean
  showFollow?: boolean
}

export const VendorListItem: React.FC<VendorListItemProps> = ({
  item,
  href,
  topic,
  subTopic,
  companyName,
  showCompare = true,
  showFollow = true,
}) => {
  const content = removeTags(item.description || '')
  const isPremium = !!item.isPremium
  const isEnhanced = isPremium || !!item.isEnhanced
  const subTopics = item.subTopics?.filter((subTopic): subTopic is string => !!subTopic) || []
  const [showSignUp, setShowSignUp] = useState(false)
  const [showEmailVerify, setShowEmailVerify] = useState(false)
  const solutionType = convertSolutionItemTypeToSolutionType(item.type)
  const isCompanyListing = solutionType === null
  const displayName = isCompanyListing ? `${item.displayName} - Company` : item.displayName

  const { session } = useAuthenticated()
  const email = session?.user.email || ''
  const handleCloseSignUp = () => {
    setShowSignUp(false)
  }

  const handleCloseEmailVerify = () => {
    setShowEmailVerify(false)
  }

  return (
    <div className="flex flex-row items-center pt-[14px] pb-[8px]">
      <div className="flex flex-row w-full md:items-center">
        <LinkWithOrigin
          toUrl={href}
          topicName={topic?.attributes?.name}
          subTopicName={subTopic?.attributes?.name}
          companyName={companyName}
        >
          <VendorLogo
            className="bg-white rounded-[.5rem] drop-shadow-md md:min-w-[120px] md:w-[120px] md:h-[120px] w-[80px] h-[80px] flex items-center justify-center object-contain"
            src={item.logo}
          />
          <Hidden mdUp>
            <ThemeOnly theme="legaltech">
              {isPremium && <PremiumTag className="mt-[10px] w-[80px] !p-0" variant="yellow" />}
              {!isPremium && isEnhanced && <EnhancedTag className="mt-[10px] w-[80px] !p-0" />}
            </ThemeOnly>
            <ThemeOnly theme="ilta">
              {(isPremium || isEnhanced) && <EnhancedTag className="mt-[10px] w-[80px] !p-0" />}
            </ThemeOnly>
          </Hidden>
        </LinkWithOrigin>
        <div className="grow flex-1 ml-[15px] md:ml-[1.75rem]">
          <div className="w-full flex">
            <div className="flex-1">
              <div className="w-full flex md:flex-row flex-col justify-between">
                <div className="w-full flex items-center mb-1 flex-wrap">
                  <LinkWithOrigin
                    toUrl={href}
                    className="cursor-pointer md:text-[1.125rem] text-[1.0rem] text-textdark font-secondary font-bold mb-1.5"
                    topicName={topic?.attributes?.name}
                    subTopicName={subTopic?.attributes?.name}
                    companyName={companyName}
                  >
                    {displayName}
                    <Hidden mdDown>
                      <ThemeOnly theme="legaltech">
                        {isPremium && <PremiumTag className="ml-[10px] inline-block" variant="yellow" />}
                        {!isPremium && isEnhanced && <EnhancedTag className="ml-[10px] inline-block" />}
                      </ThemeOnly>
                      <ThemeOnly theme="ilta">
                        {(isPremium || isEnhanced) && <EnhancedTag className="ml-[10px] inline-block" />}
                      </ThemeOnly>
                    </Hidden>
                  </LinkWithOrigin>
                  {solutionType && SHOW_REVIEWS && (
                    <div className="w-full flex md:flex-row flex-col">
                      <div className="flex flex-row">
                        <ThemeOnly theme="legaltech">
                          <ReviewWithLinkItem
                            reviewCnt={item.reviewCnt || 0}
                            starCnt={item.rating || 0}
                            title={displayName}
                            solutionType={solutionType}
                            id={`${item.id || 0}`}
                            isEnhanced={isEnhanced}
                            href={href || ''}
                          />
                        </ThemeOnly>
                      </div>
                    </div>
                  )}
                </div>
                {solutionType && (
                  <ThemeOnly theme="legaltech">
                    <Hidden mdDown>
                      <div className="flex flex-row">
                        {showCompare && (
                          <CompareButton className="items-start" slug={item.slug || ''} solutionType={solutionType} />
                        )}

                        {showFollow && (
                          <FollowButton
                            solutionType={solutionType}
                            id={(item.id || 0).toString() || ''}
                            isButton={false}
                            displayName={displayName}
                            isEnhanced={isEnhanced}
                            follow={item.follow}
                            isFromVendorList={true}
                          />
                        )}
                      </div>
                    </Hidden>
                  </ThemeOnly>
                )}
              </div>
              <HtmlContentViewer className="text-[0.815rem] md:pr-[2rem] showmore-text" html={content} />
              {solutionType && (
                <ThemeOnly theme="legaltech">
                  <Hidden mdUp>
                    <div className="flex items-center mt-[12px]">
                      <CompareButton
                        className="items-start h-[unset] w-[unset]"
                        slug={item.slug || ''}
                        solutionType={solutionType}
                      />
                      <FollowButton
                        solutionType={solutionType}
                        id={(item.id || 0).toString() || ''}
                        isButton={false}
                        displayName={displayName}
                        isEnhanced={isEnhanced}
                        follow={item.follow}
                        isFromVendorList={true}
                      />
                    </div>
                  </Hidden>
                </ThemeOnly>
              )}
            </div>
            <ThemeOnly theme="ilta">
              {item.iltaSponsorshipLogo && (
                <Image src={item.iltaSponsorshipLogo} width={200} height={200} alt="" className="h-[70px] w-auto" />
              )}
            </ThemeOnly>
          </div>
          <CategoryList className="mb-[6px]" listCategory={subTopics} />
        </div>
      </div>
      <SignUpModal isOpen={showSignUp} onClose={handleCloseSignUp} />
      <VerifyEmailModal isOpen={showEmailVerify} email={email} onClose={handleCloseEmailVerify} />
    </div>
  )
}
